import React from 'react';
import { Box, Container, Stack } from '@mui/material';

import { DashboardHero } from './DashboardHero';
import { StatStrip } from './StatStrip';
import { RecentAssetsRow } from './RecentAssetsRow';
import { LabShowcase } from './LabShowcase';
import { StoriesCard } from './StoriesCard';
import { isStoriesFeedEnabled } from './featureFlags';
import { SettingsMenu } from '../../components/common/SettingsMenu';
import { CreditChip } from '../../credits/CreditChip';
import { useLabs } from '../../queries';

/**
 * Dashboard — default landing surface (`/dashboard`, redirected from `/`).
 *
 * Laid out as a Creative-Cloud-style home: start something, see what you've
 * made, then what you can make with.
 *
 *   1. Hero — greeting + quick-create actions, on a theme-native gradient.
 *   2. Stat strip — assets created, split by kind.
 *   3. Jump back in — recent assets as a thumbnail grid.
 *   4. Your labs — capability cards with operation counts + studio deep-links.
 *   5. Discover — the creative-markets feed (flagged off until it has a
 *      real data source; see featureFlags.isStoriesFeedEnabled).
 *
 * Every section is responsive by flex-wrap rather than breakpoint branching, so
 * the page reflows continuously instead of snapping between fixed layouts.
 */
export const DashboardRoute: React.FC = () => {
  // Resolved once here and handed to the hero, so its quick-create actions and
  // the lab cards agree about which labs the SMA-13 gate currently exposes.
  const { data: labsData } = useLabs();
  const enabledLabIds = labsData?.labs.map(l => l.id);

  return (
    <Box
      aria-label="Dashboard"
      sx={{
        // Global index.css pins html/body/#root to overflow:hidden (for the
        // full-screen lab views). The dashboard is a normal long page, so it
        // owns its own vertical scroll here — otherwise everything below the
        // hero is clipped and unreachable.
        height: '100vh',
        overflowY: 'auto',
        bgcolor: 'background.default',
        pb: 8,
      }}
    >
      <Container maxWidth="xl" sx={{ pt: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mb: 2 }}>
          {/* Balance first: it's the only payment entry point, so it has to be
              visible without opening a menu. Hidden entirely in shadow mode. */}
          <CreditChip />
          {/* One control for everything account-shaped: who you are, settings,
              and signing in or out. It used to be a cog beside a separate
              account button, which left it arbitrary which one held what. */}
          <SettingsMenu />
        </Stack>

        <Stack spacing={{ xs: 4, md: 5 }}>
          <DashboardHero enabledLabIds={enabledLabIds} />
          <StatStrip />
          <RecentAssetsRow />
          <LabShowcase />
          {/* Hidden until a real feed exists — the shelf otherwise renders
              synthetic stories attributed to real publishers. See featureFlags. */}
          {isStoriesFeedEnabled() && <StoriesCard items={[]} />}
        </Stack>
      </Container>
    </Box>
  );
};
